import React, { useState } from 'react';
import { Table, InputNumber } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { StrategyPhase, WeeklyStrategy } from '../ProfitScenario/types';
import { WeeklyTaskList } from './WeeklyTaskList';
import { WeekColumn } from './columns/WeekColumn';
import { DiscountColumn } from './columns/DiscountColumn';
import { AdsColumn } from './columns/AdsColumn';
import { MetricsColumn } from './columns/MetricsColumn';
import { MarketingActivitiesColumn } from './columns/MarketingActivitiesColumn';
import { getPhaseTheme } from './PhaseHeader';

interface StrategyPhaseTableProps {
    phase: StrategyPhase;
    landedCost: number;
    fbaFee: number;
    referralRate: number;
    storageFee: number;
    shippingCost: number; // 新增
    updateWeekStrategy: (phaseId: string, weekIndex: number, updates: Partial<WeeklyStrategy>) => void;
    addTask: (phaseId: string, weekIndex: number, content: string, priority: 'high' | 'medium' | 'low', startDate: string, endDate: string) => void;
    updateTask: (phaseId: string, weekIndex: number, taskId: string, updates: Partial<{ content: string; priority: 'high' | 'medium' | 'low' }>) => void;
    deleteTask: (phaseId: string, weekIndex: number, taskId: string) => void;
    toggleTaskCompletion: (phaseId: string, weekIndex: number, taskId: string) => void;
    clearCompletedTasks: (phaseId: string, weekIndex: number) => void;
}

export const StrategyPhaseTable: React.FC<StrategyPhaseTableProps> = ({
    phase,
    landedCost,
    fbaFee,
    referralRate,
    storageFee,
    shippingCost, // 新增
    updateWeekStrategy,
    addTask,
    updateTask,
    deleteTask,
    toggleTaskCompletion,
    clearCompletedTasks
}) => {
    // 默认折叠所有行（用户可以点击展开需要的周次）
    const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([]);

    const getColumns = (phase: StrategyPhase): ColumnsType<WeeklyStrategy> => [
        {
            title: '周次',
            key: 'week',
            width: 100,
            fixed: 'left',
            render: (_, week) => <WeekColumn week={week} phase={phase} />
        },
        {
            title: '基础售价',
            key: 'basePrice',
            width: 90,
            render: (_, week, index) => (
                <InputNumber
                    size="small"
                    prefix="$"
                    value={week.basePrice}
                    onChange={v => updateWeekStrategy(phase.id, index, { basePrice: v || 39.99 })}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: '总日销',
            key: 'dailyUnits',
            width: 80,
            render: (_, week, index) => (
                <InputNumber
                    size="small"
                    value={week.dailyUnits}
                    onChange={v => updateWeekStrategy(phase.id, index, { dailyUnits: v || 20 })}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: '折扣策略',
            key: 'discounts',
            width: 350,
            render: (_, week, index) => (
                <DiscountColumn
                    week={week}
                    phaseId={phase.id}
                    index={index}
                    updateWeekStrategy={updateWeekStrategy}
                />
            )
        },
        {
            title: '广告策略',
            key: 'ads',
            width: 660,
            render: (_, week, index) => (
                <AdsColumn
                    week={week}
                    phase={phase}
                    index={index}
                    landedCost={landedCost}
                    fbaFee={fbaFee}
                    referralRate={referralRate}
                    storageFee={storageFee}
                    updateWeekStrategy={updateWeekStrategy}
                />
            )
        },
        {
            title: '营销活动',
            key: 'marketing',
            width: 250,
            render: (_, week, index) => (
                <MarketingActivitiesColumn
                    activities={week.marketingActivities}
                    landedCost={landedCost}
                    fbaFee={fbaFee}
                    shippingCost={shippingCost}
                    onUpdate={(activities) => updateWeekStrategy(phase.id, index, { marketingActivities: activities })}
                />
            )
        },
        {
            title: '预估数据',
            key: 'metrics',
            width: 300,
            render: (_, week) => (
                <MetricsColumn
                    week={week}
                    phase={phase}
                    landedCost={landedCost}
                    fbaFee={fbaFee}
                    referralRate={referralRate}
                    storageFee={storageFee}
                />
            )
        }
    ];

    // 渲染展开行内容（任务列表）
    const expandedRowRender = (week: WeeklyStrategy, index: number) => {
        const weekNumber = week.weekNumber;
        const taskCount = week.tasks?.length || 0;
        const completedCount = week.tasks?.filter(task => task.completed).length || 0;

        const phaseTheme = getPhaseTheme(phase.name);

        return (
            <div style={{
                margin: '-1px 0',
                background: '#ffffff',
                borderLeft: `3px solid ${phaseTheme.borderColor}`,
                borderRight: '1px solid #e8e8e8',
                borderBottom: '1px solid #e8e8e8'
            }}>
                {/* 周期标题栏 - 比阶段标题浅一个层次 */}
                <div style={{
                    padding: '8px 24px',
                    background: phaseTheme.bgColor,
                    borderBottom: `1px solid ${phaseTheme.borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{
                            fontSize: 14,
                            fontWeight: 500,
                            color: phaseTheme.textColor
                        }}>
                            第 {weekNumber} 周任务
                        </span>
                        <span style={{
                            fontSize: 11,
                            color: '#666',
                            background: '#fff',
                            padding: '2px 8px',
                            borderRadius: 10,
                            border: '1px solid #d9d9d9'
                        }}>
                            {completedCount} / {taskCount} 已完成
                        </span>
                    </div>
                </div>

                {/* 任务列表 */}
                <div style={{ padding: '16px 24px' }}>
                    <WeeklyTaskList
                        phaseId={phase.id}
                        weekIndex={index}
                        tasks={week.tasks}
                        onAddTask={(content, priority, startDate, endDate) => addTask(phase.id, index, content, priority, startDate, endDate)}
                        onUpdateTask={(taskId, updates) => updateTask(phase.id, index, taskId, updates)}
                        onDeleteTask={(taskId) => deleteTask(phase.id, index, taskId)}
                        onToggleTask={(taskId) => toggleTaskCompletion(phase.id, index, taskId)}
                        onClearCompleted={() => clearCompletedTasks(phase.id, index)}
                    />
                </div>
            </div>
        );
    };

    return (
        <Table
            columns={getColumns(phase)}
            dataSource={phase.weeks}
            rowKey={(_, index) => index!}
            pagination={false}
            size="middle"
            scroll={{ x: 1300 }}
            bordered
            expandable={{
                expandedRowRender: (week, index) => expandedRowRender(week, index),
                expandedRowKeys: expandedRowKeys,
                onExpandedRowsChange: (keys) => setExpandedRowKeys(keys as number[])
            }}
        />
    );
};
